import { useCallback, useState } from 'react';

import type { PublicRunSnapshot } from '../../contracts';
import {
  EMPTY_TRANSCRIPT_STORE,
  ingestSnapshot,
  rotateTranscriptStore,
  type TranscriptStore,
} from '../harness/transcript-store';

/** One state object so the fetch ingest can run as a render-phase adjustment; a
 *  committed effect would show the pre-fetch transcript for a frame first. */
interface TranscriptState {
  runId: string | undefined;
  ingestedRun: PublicRunSnapshot | null | undefined;
  store: TranscriptStore;
}

export type ApplyToStore = (update: (store: TranscriptStore) => TranscriptStore) => void;

export interface TranscriptStoreState {
  store: TranscriptStore;
  applyToStore: ApplyToStore;
}

export const useTranscriptStoreState = (
  agentRunId: string | undefined,
  run: PublicRunSnapshot | null | undefined
): TranscriptStoreState => {
  const [transcriptState, setTranscriptState] = useState<TranscriptState>(() => ({
    runId: agentRunId,
    ingestedRun: null,
    store: EMPTY_TRANSCRIPT_STORE,
  }));

  // Rotation is the ONLY whole-store clear: a new run is a new chat lifetime.
  if (transcriptState.runId !== agentRunId) {
    setTranscriptState({
      runId: agentRunId,
      ingestedRun: null,
      store: rotateTranscriptStore(),
    });
  } else if (run && transcriptState.ingestedRun !== run) {
    setTranscriptState((current) =>
      current.ingestedRun === run
        ? current
        : { ...current, ingestedRun: run, store: ingestSnapshot(current.store, run) }
    );
  }

  const applyToStore = useCallback<ApplyToStore>((update) => {
    setTranscriptState((current) => {
      const store = update(current.store);
      return store === current.store ? current : { ...current, store };
    });
  }, []);

  return { store: transcriptState.store, applyToStore };
};
